import { useMemo } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { formatNumberWithCommas } from '@/modules/common/utils/format-numbers.ts';
import { useTopHoldersQuery } from '@/modules/issuer-dashboard/queries/shareholders.tsx';
import { issuerDashboardPaths } from '@/modules/issuer-dashboard/routes.tsx';
import { BodyCell, InfoCard, Table, TableBodyColumn, TableHeadColumn } from 'ui';
import { useIsMobile } from 'ui/hooks';
import { goodFormatNumber } from 'ui/utils';

type TypeHead = '#' | 'Holder' | 'Units / Shares' | 'Value' | 'Percentage';

const headItems: TableHeadColumn<TypeHead>[] = [
  { title: '#', className: '!py-2' },
  { title: 'Holder', className: 'min-w-[180px] !py-2' },
  { title: 'Units / Shares', className: '!py-2' },
  { title: 'Value', className: '!py-2' },
  { title: 'Percentage', className: '!py-2' },
];

interface TopHoldersProps {
  register_class_id?: number;
}

export const TopHolders = ({ register_class_id }: TopHoldersProps) => {
  const { isMobile } = useIsMobile();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const {
    data: dataTopHolders,
    isFetching,
    isError,
  } = useTopHoldersQuery({ register_class_id: register_class_id || undefined }, false);

  const bodyItems = useMemo<TableBodyColumn[]>(() => {
    if (!dataTopHolders?.data?.data?.length) {
      return [];
    }

    return dataTopHolders?.data?.data?.map((data, i) => {
      const body: BodyCell[] = [
        {
          cell: `${i + 1}`,
          className: '!py-2',
        },
        {
          cell: data.entity_name,
          className: 'text-[#0369A1] font-[500] font-[Inter] !py-2',
        },
        {
          cell: formatNumberWithCommas(data?.units || 0),
          className: '!py-2',
        },
        {
          cell: `$${formatNumberWithCommas(data.balance)}`,
          className: '!py-2',
        },
        {
          cell: `${goodFormatNumber(data.percentage)}%`,
          className: '!py-2',
        },
      ];

      return {
        cells: body,
        onClick: data.entity_id
          ? () =>
              navigate(
                {
                  pathname: issuerDashboardPaths.dynamic.holder(String(data.entity_id)),
                  search: params.toString(),
                },
                {
                  state: { from: pathname + '?' + params.toString() },
                },
              )
          : undefined,
      };
    });
  }, [dataTopHolders?.data?.data, navigate, params, pathname]);

  return (
    <InfoCard title="Top holders" classes={{ content: 'p-0 pt-6' }}>
      <Table
        classNameHead="bg-transparent"
        className="my-0 rounded-b-xl"
        withRounded={false}
        withBorderX={isMobile}
        withBorder={false}
        headItems={headItems}
        items={bodyItems}
        isLoading={isFetching}
        isError={isError}
      />
    </InfoCard>
  );
};
